// Price seasons: listing panel, calendar stripe, Calendar rules and the bulk "Season" action.
export const priceSeasons = {
  card: {
    title: "Seasons",
    sub: "Raise or lower prices by dates and by channel",
  },
  header: {
    eyebrow: "Prices by dates",
    title: "Price seasons",
    subtitle: "Raise or lower your prices on the dates you choose, for every channel or just some. Below you can see what you charge each day on each channel.",
    new: "New season",
  },
  notice: {
    preview: "Preview: seasons don't change the prices being sold or what reaches the OTAs yet.",
  },
  states: {
    loading: "Loading seasons…",
  },
  year: {
    title: "Your year at a glance",
    sub: "Each colour is a season. Click a month to see its prices.",
    prev: "Previous year",
    next: "Next year",
    jump: "See this month's prices",
    empty: "This year has no seasons yet.",
  },
  list: {
    title: "Your seasons",
    sub: "What each one does, when and where.",
    emptyTitle: "You don't have any seasons yet",
    emptyText: "Create one to raise prices on high-demand dates (summer, Christmas, a festival…) or lower them when things are quiet.",
    createFirst: "Create the first one",
    paused: "Paused",
    pause: "Pause",
    activate: "Activate",
    edit: "Edit",
    duplicate: "Duplicate",
    delete: "Delete",
    noEffect: "No price changes",
    everyYear: "every year",
    rateFallback: "Rate",
    channelFallback: "Channel {id}",
  },
  chips: {
    all: "{value} all channels",
    fixed: "{price} per night, all channels",
  },
  scope: {
    onlyThis: "This calendar only",
    shared: "Shared · {count} calendars",
    allCalendars: "All calendars ({count})",
    allRooms: "All room types ({count})",
    listing: "This listing",
    listingHint: "Every apartment in this listing",
    otherListing: "Another listing",
    units: "Only these units",
    unitsHint: "Choose which apartments it applies to",
    unitsCount: "1 unit | {count} units",
    account: "Whole account",
    accountHint: "All your listings and units",
  },
  calendar: {
    title: "Price calendar",
    sub: "The final price of each night on each channel. Hover (or tap) a cell to see how it's calculated.",
    prev: "Previous month",
    next: "Next month",
    noUnits: "This listing has no units with a rate yet.",
    noRate: "We couldn't find this unit's rate.",
    loading: "Calculating prices…",
    legendUp: "Raised by a season",
    legendDown: "Lowered by a season",
    legendManual: "Price set by hand that day",
    manualShort: "manual",
    ratesIn: "Prices in {currency}",
    seasonRow: "Season",
    seasonTip: "Season: {name}",
    standard: "Standard Rate",
    mainSub: "Main rate",
    usualPct: "Usual markup {value}",
    ownPrice: "Own price",
  },
  rule: {
    show: "How is it calculated?",
    hide: "Hide explanation",
    title: "This is how each night's price is calculated",
    step1: "It starts from that day's price: the one set by hand if there is one; otherwise the weekend or base price.",
    step2: "The «all channels» season is applied on top of that price.",
    step3: "Each channel then applies its usual markup (for example, Booking +21 %).",
    step4: "Finally, that channel's own season is added, if there is one.",
    overlap: "If two seasons overlap, the more specific one (the one with the shorter dates) wins. \"All channels\" and each channel are decided separately.",
  },
  tip: {
    withoutSeason: "Without season",
    base: "Base",
    dayPrice: "Day price",
    manual: "Set by hand",
    manualWins: "\"{name}\" doesn't apply: the price set by hand wins.",
    fixed: "fixed price {price}",
  },
  editor: {
    nameTitle: "Name",
    subSpecial: "An event or specific dates with their own price.",
    saveChanges: "Save changes",
    verbUp: "Up",
    verbDown: "Down",
    titleNew: "New special season",
    titleEdit: "Edit season",
    step1: "What's it called?",
    step2: "Which dates?",
    step3: "What changes?",
    step4: "Where it applies",
    namePlaceholder: "E.g. November peak",
    examplePh: "e.g. {value}",
    from: "From",
    to: "To",
    repeat: "Repeats every year",
    removeRange: "Remove these dates",
    addRange: "Add other dates",
    allChannels: "For all channels",
    allChannelsHint: "Changes the base price; each channel keeps its usual markup on top.",
    modeUp: "Raise %",
    modeDown: "Lower %",
    modeAmount: "Add amount",
    modeFixed: "Fixed price",
    hint_up: "E.g. 20 → a 100 night becomes 120.",
    hint_down: "E.g. 10 → a 100 night becomes 90.",
    hint_amount: "Added to every night. Use a negative number to subtract.",
    hint_fixed: "Every night in these dates costs this, whatever its price.",
    someChannels: "Only for some channels",
    someChannelsHint: "An extra adjustment on those channels only. Use a negative number to lower.",
    usual: "Usual markup {value}",
    channelAddsUp: "It's added on top of that channel's existing markup.",
    exampleLabel: "Example",
    exampleSame: "A {base} night stays at {base}",
    exampleChange: "A {base} night becomes {after}",
    exampleChannel: "on {channel} {price}",
    copyName: "{name} (copy)",
    cancel: "Cancel",
    save: "Save season",
    saving: "Saving…",
  },
  delete: {
    title: "Delete \"{name}\"?",
    text: "Those dates go back to their usual prices. This can't be undone.",
    confirm: "Yes, delete",
  },
  errors: {
    periodName: "Give each period a name (for example, “Easter”).",
    name: "Give it a name so you can recognise it.",
    ranges: "Enter the start and end dates.",
    rangeOrder: "The end date can't be before the start date.",
    value: "Enter how much the price changes.",
    down: "To lower, use a percentage below 100.",
    channelRange: "The channel adjustment must be between −100 % and +1000 %.",
    effect: "Choose what changes: for all channels or for some.",
    units: "Tick at least one unit.",
  },
  toast: {
    removedFrom: "Removed from 1 calendar. | Removed from {count} calendars.",
    loadError: "Seasons couldn't be loaded.",
    saveError: "Couldn't save. Please try again.",
    created: "Season created.",
    updated: "Season saved.",
    deleted: "Season deleted.",
    activated: "Season activated.",
    paused: "Season paused.",
  },
  calendarStripe: {
    tip: "Season: {name}",
    tipChannels: "Season: {name} (some channels only)",
  },
  adm: {
    seeAll: "See all {n} seasons",
    periods: "{n} periods",
    next: "next: {dates}",
    same: "Same price",
    closes: "Closes",
    someRates: "Some rates",
    title: "Seasons",
    summary: "1 season affects this unit | {count} seasons affect this unit",
    summaryNone: "No price seasons on this unit",
    empty: "There are no seasons changing this unit's price.",
    manage: "Manage seasons",
  },
  bulk: {
    label: "Season",
    hint: "Raise or lower the price of these dates",
    microcopy: "Price by dates and channel",
    group: "Price seasons",
    title: "What's the season like?",
    stepHint: "It applies to the dates and units you chose above.",
    name: "Name",
    create: "Create season",
    scopeAccount: "It will apply to the whole account.",
    scopeUnits: "It will apply to 1 unit. | It will apply to {count} units.",
    summaryAccount: "Season from {from} to {to} across the whole account.",
    summaryUnits: "Season from {from} to {to} on 1 unit. | Season from {from} to {to} on {count} units.",
  },
  // ── Redesign: seasons by tier (high, mid, low) and special ones ──
  tiers: {
    high: "High season",
    mid: "Mid season",
    low: "Low season",
    custom: "Special season",
    highShort: "High",
    midShort: "Mid",
    lowShort: "Low",
    customShort: "Special",
  },
  yourYear: {
    colSeason: "Season",
    colPeriods: "Periods",
    colAdjust: "Adjustment",
    colActions: "Actions",
    manage: "Manage",
    morePeriods: "+{count} more",
    exampleShort: "{base} → {after}",
    title: "Your year's seasons",
    sub: "When demand is higher or lower, and how much your price goes up or down at each time.",
    basisFallback: "Based on your bookings and demand in your area",
    suggested: "Suggested",
    active: "Active",
    paused: "Paused",
    occupancy: "Occupancy {value}%",
    occupancyUnknown: "No occupancy data yet",
    editDates: "Edit dates",
    edit: "Edit",
    apply: "Apply seasons",
    applying: "Applying…",
    applyHint: "You can change the percentages before applying. They only apply to this listing and you can pause them at any time.",
    applied: "Seasons applied.",
    allApplied: "Your seasons are applied. Change the percentage whenever you like: it saves automatically.",
    noDates: "No dates this year",
    raise: "Up {value}%",
    lower: "Down {value}%",
    same: "Same price, just marks the period",
    less: "Lower 5%",
    more: "Raise 5%",
    example: "A night at {base} → {after}",
    otherAdjust: "Adjustment: {value}",
    saved: "Adjustment saved.",
    noSuggestions: "There isn't enough data to detect your seasons yet. You can create them yourself with “Add season”.",
  },
  special: {
    emptyShort: "No special seasons yet.",
    addNew: "New special season",
    title: "Special seasons",
    sub: "Festivals, events, long weekends… specific dates with their own price.",
    add: "Add season",
    empty: "You don't have special seasons yet. Add one for an event or specific dates.",
  },
  yearLegend: {
    custom: "Special",
    suggested: "Suggested, not applied",
    dominant: "Mostly: {tier}",
  },
  editorType: {
    label: "Season type",
    hint: "High, mid and low show with their colour across the whole calendar. “Special” is for events and specific dates.",
  },
  stripeTip: {
    standardPrice: "Standard rate: {from} → {to}",
    standardSame: "Standard rate: unchanged",
    standardClosed: "Standard rate: closed",
    allChannelsSame: "All channels: same price",
    allChannels: "All channels {value}",
    onlyIn: "Only on: {channels}",
    previewNote: "Preview: the published price doesn't change yet",
    range: "{from} – {to}",
    noChange: "No price change",
    ratePrice: "{rate}: {from} → {to}",
    rateClosed: "{rate}: closed",
  },
  bulkType: {
    label: "Type",
  },
  tiersPlural: {
    high: "High seasons",
    mid: "Mid seasons",
    low: "Low seasons",
  },
  manage: {
    high: "Manage high seasons",
    mid: "Manage mid seasons",
    low: "Manage low seasons",
    highSub: "The busiest dates of the year. Add one period for each: Easter, summer, Christmas…",
    midSub: "Dates with normal demand. You can keep the same price and use them just to mark the period.",
    lowSub: "The quietest dates, when lowering the price helps attract bookings.",
  },
  periods: {
    title: "Periods",
    hint: "Each period has a name and dates. For example: “Easter”, from March 29 to April 6.",
    generated: "Period {n}",
    noDates: "No dates",
    name: "Period name",
    namePh: "E.g.: Easter",
    add: "Add period",
    remove: "Remove",
    done: "Done",
    empty: "No periods yet. Add the first one.",
    showSuggestions: "See suggestions",
    hideSuggestions: "Hide suggestions",
    suggestionsHint: "Tap one to add it with its name and dates. You can change them afterwards.",
  },
  price: {
    title: "Price",
    up: "Up",
    down: "Down",
    sameShort: "Same price",
    same: "The price doesn't change on these dates: the season only marks the period.",
    sentenceUp: "On these dates, the price goes up",
    sentenceDown: "On these dates, the price goes down",
    sentenceAmount: "On these dates, each night adds",
    sentenceFixed: "On these dates, each night costs",
    onlyChannels: "The base price doesn't change: only the channels chosen in “Advanced options” are adjusted.",
  },
  advanced: {
    title: "Advanced options",
    sub: "Fixed amounts, specific channels and where it applies",
    howTitle: "How the price is adjusted",
    percent: "Percentage",
  },
  holidays: {
    showPast: "Show 1 past date | Show {count} past dates",
    hidePast: "Hide past dates",
    title: "Holidays & events · {country} {year}",
    sub: "High-demand dates in your country. Apply the ones you want as a special season, with the adjustment you choose.",
    country: "Country",
    colDate: "Date",
    colName: "Holiday or event",
    colAdjust: "Adjustment",
    kindHoliday: "Holiday",
    kindBridge: "Long weekend",
    kindPeriod: "Season",
    highDemand: "High demand",
    apply: "Apply",
    applied: "Applied",
    remove: "Remove",
    applyHigh: "Apply high-demand dates",
    bulkHint: "1 high-demand date not applied yet. | {count} high-demand dates not applied yet.",
    appliedToast: "“{name}” applied as a special season.",
    appliedMany: "1 special season created. | {count} special seasons created.",
    empty: "No holidays or events for this country and year.",
    badge: "Holiday",
  },
  closures: {
    sectionTitle: "Closures by season",
    sectionSub: "Dates when you stop selling, on all rates or only some.",
    empty: "You have no closed dates.",
    add: "Close dates",
    whatTitle: "What do you close?",
    namePh: "E.g.: Bathroom renovation",
    titleNew: "Close dates",
    titleEdit: "Edit closure",
    editorSub: "Choose the dates and which rates stop selling. The price doesn't change.",
    title: "Close sales on these dates",
    hint: "Nobody can book these nights on the rates you choose. You can use it without changing the price.",
    all: "All rates",
    some: "Only some rates",
    allHint: "All rates and all channels are closed.",
    someHint: "Only the checked rates are closed; the rest stay on sale.",
    mainRate: "Main rate ({name})",
    pickOne: "Check at least one rate to close.",
    tagAll: "Closes everything",
    tagSome: "Closes: {rates}",
    closedShort: "Closed",
    closed: "Closed by a season",
    closedBy: "Closed by “{name}”",
    popupAll: "Sales closed: all rates",
    popupSome: "Closed: {rates}",
  },
  tierCount: {
    high: "high season set up | high seasons set up",
    mid: "mid season set up | mid seasons set up",
    low: "low season set up | low seasons set up",
  },
  tierCountSug: {
    high: "suggested high season | suggested high seasons",
    mid: "suggested mid season | suggested mid seasons",
    low: "suggested low season | suggested low seasons",
  },
  tierCard: {
    next: "Next",
    noneUpcoming: "No periods left this year",
    preview: "Preview",
    configure: "Configure",
    close: "Close",
    colPeriod: "Period",
  },
  picker: {
    fromEvent: "Pick a holiday or event",
    fromEventSub: "Key dates in {country}",
    manual: "Create one manually",
    manualSub: "You set the name and dates",
    search: "Search: Christmas, Carnival…",
    countryLine: "Holidays and events in {country} · {year}",
    notYourCountry: "Not your country? Change",
    added: "Added",
    noResults: "No matching holidays.",
  },
  rules: {
    title: "What changes?",
    hint: "One rule per change: which rates it affects and what it does to them.",
    add: "Add rule",
    remove: "Remove rule",
    empty: "No rules yet. Add the first one.",
    whichRates: "Which rates?",
    allRates: "All rates",
    someRates: "Choose rates",
    searchRates: "Search rate…",
    filterAll: "All",
    filterNone: "No channel",
    noRates: "No matching rates.",
    whatToDo: "What do we do?",
    actUp: "Raise %",
    actDown: "Lower %",
    actAmount: "Add amount",
    actClose: "Close sales",
    value: "Value",
    closeHint: "Those rates can't be booked on these dates.",
    incomplete: "Finish this rule: pick rates, what to do and the value.",
    principal: "Main",
    derived: "Derived",
    derivedFrom: "Derived from {name}",
    usual: "markup {value}",
    wholeChannel: "All rates on this channel",
    channelAll: "{channel} · all its rates",
    noRateYet: "No rates chosen",
    sumUp: "up {value} %",
    sumDown: "down {value} %",
    sumPlus: "adds {value}",
    sumMinus: "subtracts {value}",
    sumFixed: "costs {value}",
    sumClosed: "closed",
    sentenceUp: "The price goes up",
    sentenceDown: "The price goes down",
  },
  draft: {
    period: "Show period:",
    see: "See how it looks",
    back: "Back to editing",
    apply: "Apply season",
    title: "This is how it would look",
    sub: "{apt}, from {from} to {to}. Nothing has been saved yet.",
    noChanges: "With these rules no price changes on these dates.",
    up: "{n} night goes up | {n} nights go up",
    down: "{n} night goes down | {n} nights go down",
    closed: "{n} night closed | {n} nights closed",
    cellChange: "Before {before} → now {after}",
    cellSame: "{price} (no change)",
    cellClosed: "Closed (before {before})",
    hoverHint: "Hover a price to see before and after.",
    unavailable: "Preview isn't available yet. You can still apply the season.",
  },
  chart: {
    show: "Show chart",
    hide: "Hide chart",
    title: "Nightly price · {year}",
    titleDraft: "Nightly price",
    rate: "Rate",
    base: "Base price",
    current: "Current",
    withSeason: "With this season",
    vsBase: "{value} vs base",
    closed: "Closed",
    loading: "Loading the year…",
    error: "Couldn't load the chart.",
    noSeason: "No season",
  },
  calendarCell: {
    before: "Before {price} · {season}",
    closed: "Closed by the season «{season}»",
    closedShort: "Closed",
  },
  bulkStd: {
    note: "In bulk edit the season applies to each property's Standard rate (the one in the calendar). To change other rates (Booking, Non-refundable…), use «Seasons» in the listing settings.",
  },
  legend: {
    button: "Seasons",
    title: "Seasons in the calendar",
    high: "High season",
    mid: "Mid season",
    low: "Low season",
    special: "Special season or holiday",
    someRates: "Only changes some rates (not Standard)",
    up: "raised by a season",
    down: "lowered by a season",
    hover: "Hover the price or the dot to see the season and how much it changes.",
  },
  target: {
    hintHotel: "All the hotel's rooms are shown; in each season you choose which ones it applies to.",
    rooms: "{count} room type | {count} room types",
    hint: "Each calendar has its own seasons.",
    label: "You are setting up",
    labelHotel: "Hotel",
    search: "Search calendar…",
    count: "No seasons | 1 season | {count} seasons",
    none: "No calendar matches.",
  },
  where: {
    title: "Which calendars",
    titleHotel: "Which room types",
    hint: "Tick the calendars that have this season. Unticking one removes it from that calendar.",
    this: "this one",
    all: "Select all ({count})",
    onlyThis: "Only this one",
    onlyThisHotel: "Only this hotel",
    allHotel: "Whole hotel",
    noneHotel: "Clear hotel",
    account: "This season belongs to your whole account: it also applies to your other listings.",
    errorNone: "Choose at least one calendar.",
  },
  ask: {
    title: {
      change: "Where should the change apply?",
      pause: "Where should “{name}” be paused?",
      activate: "Where should “{name}” be activated?",
      delete: "Remove “{name}” from where?",
      create: "Which calendars should get the seasons?",
    },
    text: "“{name}” is shared by {count} calendars.",
    textNew: "Each calendar has its own seasons.",
    onlyThis: "Only in “{name}”",
    onlyThisHint: "The other calendars stay as they are.",
    all: "In all {count} calendars",
    removeThis: "Remove it only from “{name}”",
    removeThisHint: "The other calendars keep it.",
    removeAll: "Delete it from all {count} calendars",
    pick: "Choose calendars",
    pickHint: "Tick which ones.",
    confirm: "Apply",
  },
};
